// src/pages/ForgotPassword.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Form, Row, Alert, Spinner } from "react-bootstrap";
import logo from "../assets/img/IMG-20250130-WA0008.jpg";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  /* ============================================================
     HANDLERS
     ============================================================ */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setError("Veuillez saisir votre adresse email");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Veuillez saisir une adresse email valide");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const response = await fetch(`${API_BASE_URL}/forgot_password.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => navigate("/pages/signin2"), 2500);
      } else {
        setError(data.message || "Une erreur est survenue");
      }
    } catch (err) {
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     RENDU
     ============================================================ */
  return (
    <>
      <div className="auth-page-charte">
        <div className="container">
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="auth-card-charte">

                {/* ===== HEADER CHARTE ===== */}
                <div className="auth-header-charte">
                  <div className="auth-logo-wrapper-charte">
                    <img src={logo} className="auth-logo-charte bg-white" alt="Logo" />
                  </div>
                  <h3 className="auth-title-charte">Mot de passe oublié ?</h3>
                  <p className="auth-subtitle-charte">
                    Ne vous inquiétez pas, nous sommes là pour vous aider
                  </p>
                </div>

                {/* ===== BODY ===== */}
                <div className="auth-body-charte">

                  {error && (
                    <Alert variant="danger" onClose={() => setError("")} dismissible className="alert-error-charte mb-4">
                      <div className="d-flex align-items-center">
                        <i className="ri-error-warning-line fs-4 me-2"></i>
                        <div className="fw-semibold">{error}</div>
                      </div>
                    </Alert>
                  )}

                  {success && (
                    <div className="alert-success-charte mb-4">
                      <div className="d-flex align-items-start">
                        <div className="alert-success-icon-charte">
                          <i className="ri-mail-check-line"></i>
                        </div>
                        <div>
                          <strong>Email envoyé avec succès !</strong>
                          <p className="mb-1 mt-1 small">
                            Un email de réinitialisation vous a été envoyé. Vérifiez votre boîte de réception.
                          </p>
                          <small className="text-muted">
                            Redirection vers la page de connexion dans quelques secondes...
                          </small>
                        </div>
                      </div>
                    </div>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label-modern">
                        <i className="ri-mail-line me-2"></i>
                        Adresse Email <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="input-icon-wrapper-charte">
                        <i className="ri-mail-line input-icon-charte"></i>
                        <Form.Control
                          type="email"
                          placeholder="exemple@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          disabled={loading || success}
                          size="lg"
                          required
                          className="input-with-icon-charte"
                        />
                      </div>
                      <Form.Text className="text-muted small">
                        Nous enverrons un lien de réinitialisation à cette adresse.
                      </Form.Text>
                    </Form.Group>

                    <div className="d-grid gap-3">
                      <Button
                        type="submit"
                        className="btn-modern-primary w-100 justify-content-center py-3"
                        disabled={loading || success}
                      >
                        {loading ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            Envoi en cours...
                          </>
                        ) : (
                          <>
                            <i className="ri-send-plane-line me-2"></i>
                            Envoyer le lien de réinitialisation
                          </>
                        )}
                      </Button>

                      <Button
                        variant="link"
                        onClick={() => navigate("/pages/signin2")}
                        className="back-link-charte"
                      >
                        <i className="ri-arrow-left-line me-2"></i>
                        Retour à la connexion
                      </Button>
                    </div>
                  </Form>

                  {/* ===== INFO BOX ===== */}
                  <div className="info-banner-charte mt-4">
                    <div className="info-banner-icon-charte"
                         style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                      <i className="ri-shield-check-line"></i>
                    </div>
                    <div>
                      <h6 className="fw-semibold mb-1">Sécurité garantie</h6>
                      <p className="small text-muted mb-0">
                        Vos informations sont protégées et resteront confidentielles.
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* ===== STYLES CHARTE ===== */}
      <style>{`
        :root {
          --primary: ${COLORS.primary || '#295AA7'};
          --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
          --secondary: ${COLORS.secondary || '#21ACCD'};
          --success: ${COLORS.success || '#10b981'};
          --danger: ${COLORS.danger || '#ef4444'};
          --warning: ${COLORS.warning || '#f59e0b'};
          --info: ${COLORS.info || '#3b82f6'};
        }

        /* ===== PAGE ===== */
        .auth-page-charte {
          min-height: 100vh;
          display: flex;
          align-items: center;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          padding: 2rem;
        }

        /* ===== CARD ===== */
        .auth-card-charte {
          border: none;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          animation: fadeInUp 0.5s ease-out;
        }
        .auth-card-charte:hover {
          transform: translateY(-5px);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
        }

        /* ===== HEADER ===== */
        .auth-header-charte {
          background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
          padding: 2rem;
          text-align: center;
          color: white;
          position: relative;
          overflow: hidden;
        }
        .auth-header-charte::before {
          content: '';
          position: absolute;
          top: -50px;
          right: -50px;
          width: 150px;
          height: 150px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
        }
        .auth-header-charte::after {
          content: '';
          position: absolute;
          bottom: -50px;
          left: -50px;
          width: 200px;
          height: 200px;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 50%;
        }

        .auth-logo-wrapper-charte {
          margin-bottom: 1.5rem;
          position: relative;
          z-index: 1;
        }
        .auth-logo-charte {
          height: 70px;
          
        
         
          transition: all 0.3s ease;
        }
        .auth-logo-charte:hover {
          transform: scale(1.05);
        }

        .auth-title-charte {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          position: relative;
          z-index: 1;
        }
        .auth-subtitle-charte {
          opacity: 0.9;
          margin-bottom: 0;
          font-size: 0.9rem;
          position: relative;
          z-index: 1;
        }

        /* ===== BODY ===== */
        .auth-body-charte {
          padding: 2rem;
          background: white;
        }

        /* ===== LABEL ===== */
        .form-label-modern {
          font-weight: 600;
          color: #334155;
          font-size: 0.85rem;
          margin-bottom: 0.4rem;
        }

        /* ===== INPUT ===== */
        .input-icon-wrapper-charte {
          position: relative;
          margin-bottom: 0.5rem;
        }
        .input-icon-charte {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--secondary);
          font-size: 1.1rem;
          z-index: 2;
          pointer-events: none;
        }
        .input-with-icon-charte {
          padding-left: 2.75rem !important;
          border-radius: 12px;
          border: 2px solid #e2e8f0;
          transition: all 0.3s ease;
          font-size: 1rem;
        }
        .input-with-icon-charte:focus {
          border-color: var(--secondary) !important;
          box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
          outline: none;
        }
        .input-with-icon-charte:disabled {
          background-color: #f8fafc;
          cursor: not-allowed;
        }

        /* ===== BOUTON PRIMAIRE ===== */
        .btn-modern-primary {
          background: linear-gradient(135deg, var(--primary), var(--primary-dark));
          border: none;
          color: white;
          padding: 0.75rem 1.6rem;
          border-radius: 50px;
          font-weight: 600;
          font-size: 0.95rem;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(41, 90, 167, 0.3);
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .btn-modern-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 18px rgba(41, 90, 167, 0.4);
          color: white;
        }
        .btn-modern-primary:disabled {
          opacity: 0.6;
          transform: none;
          cursor: not-allowed;
        }

        /* ===== BACK LINK ===== */
        .back-link-charte {
          color: #64748b;
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 500;
          transition: all 0.3s ease;
          padding: 0.5rem;
          border-radius: 50px;
        }
        .back-link-charte:hover {
          color: var(--secondary);
          transform: translateX(-4px);
        }

        /* ===== ALERT ERROR ===== */
        .alert-error-charte {
          border: none;
          background: #fef2f2;
          border-left: 4px solid var(--danger);
          border-radius: 12px;
          padding: 1rem 1.25rem;
        }

        /* ===== ALERT SUCCESS ===== */
        .alert-success-charte {
          background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
          border-left: 4px solid var(--success);
          border-radius: 12px;
          padding: 1rem 1.25rem;
        }
        .alert-success-icon-charte {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: rgba(16, 185, 129, 0.15);
          color: var(--success);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.3rem;
          flex-shrink: 0;
          margin-right: 1rem;
        }

        /* ===== INFO BANNER ===== */
        .info-banner-charte {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1rem 1.25rem;
          background: #f8fafc;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
        }
        .info-banner-icon-charte {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.3rem;
          flex-shrink: 0;
        }

        /* ===== ANIMATIONS ===== */
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 768px) {
          .auth-page-charte { padding: 1rem; }
          .auth-body-charte { padding: 1.5rem; }
          .auth-title-charte { font-size: 1.5rem; }
          .auth-logo-charte { height: 60px; width: 60px; }
        }
      `}</style>
    </>
  );
}
